import { ObjectId } from 'bson';
import { runInAction, set } from 'mobx';
import { observer, useLocalObservable } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import { ensurePredicateKeys, EUsageMetrics, type Segment } from '@feathr/blackbox';
import {
  Button,
  Fieldset,
  Input,
  Layout,
  ModalV1,
  showError,
  TanStackTable,
  Toolbar,
} from '@feathr/components';
import { useCollectionTable } from '@feathr/extender/hooks';
import { useStore, useUsageAndQuota } from '@feathr/extender/state';
import { getIconForAction } from '@feathr/hooks';

import Page from '../Page';
import ConversionPixelsColumns from './ConversionPixelsColumns';
import { renderConversionPixelUsage } from './ConversionPixelUsageTable';

interface IState {
  newSegment?: Segment;
  showModal: boolean;
}

function DataConversionPixelsPage(): JSX.Element {
  const state = useLocalObservable<IState>(() => ({
    newSegment: undefined,
    showModal: false,
  }));
  const { Goals, Segments } = useStore();
  const { t } = useTranslation();
  const { accountId } = useParams<{ accountId: string }>();
  const { getUsageText } = useUsageAndQuota(accountId, [EUsageMetrics.ConversionPixels]);

  function addConversionPixel(): void {
    const segmentId = new ObjectId().toHexString();
    const newSegment = Segments.create({
      id: segmentId,
      read_only: true,
      lookback_mode: 'unbounded',
      predicates: [
        ensurePredicateKeys({
          attr_type: 'string',
          attr_against: 'seg_id',
          comparison: 'eq',
          kind: 'activity',
          value: segmentId,
        }),
      ],
      is_conversion_segment: true,
    });
    runInAction(() => {
      set(state, { newSegment, showModal: true });
    });
  }

  function close(): void {
    runInAction(() => {
      set(state, { showModal: false });
    });
  }

  async function confirm(): Promise<void> {
    if (state.newSegment) {
      const newSegment = await Segments.add(state.newSegment);
      if (newSegment.error) {
        showError(newSegment.error, t);
        return;
      }

      Segments.refreshApiCache();
      runInAction(() => {
        set(state, { newSegment: undefined, showModal: false });
      });
    }
  }

  const name = state.newSegment?.get('name');
  const validationErrors = useMemo(() => {
    return state.newSegment?.validate(['name'], false).errors;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.newSegment, name]);

  const filters = useMemo(() => ({ is_conversion_segment: true, is_archived__ne: true }), []);
  // initialPageSize 20 matches the legacy Table default; the hook's own default is 10.
  const tableProps = useCollectionTable<Segment>(Segments, {
    defaultParams: { filters },
    initialPageSize: 20,
  });
  const columns = useMemo(() => ConversionPixelsColumns({ Goals, t }), [Goals, t]);

  return (
    <Page
      actions={
        <Toolbar>
          <Button onClick={addConversionPixel} prefix={getIconForAction('add')} type={'primary'}>
            {t('Add conversion pixel')}
          </Button>
        </Toolbar>
      }
      collapseHeader={false}
      collapseHeaderOnScroll={true}
      description={
        <Layout width={'max'}>
          <Trans t={t}>
            <p>
              Create a conversion pixel for precise conversion tracking, including dynamic amount
              tracking. Share the code and{' '}
              <a href={'https://help.feathr.co/hc/en-us/articles/1500002693961'} target={'_blank'}>
                written guide
              </a>{' '}
              with your developer so that it is placed correctly. Then activate it in by using
              "Advanced" conversion tracking in campaigns. Reuse the same pixel for similar
              categories of conversions like donations, events, registrations, and more.
            </p>
          </Trans>
        </Layout>
      }
      secondaryText={getUsageText()}
      title={t('Conversion Pixels')}
    >
      <TanStackTable<Segment>
        {...tableProps}
        columns={columns}
        noDataText={t('No conversion pixels yet.')}
        renderSubComponent={renderConversionPixelUsage}
      />
      {state.showModal && (
        <ModalV1
          confirmDisabled={!!validationErrors?.length}
          controlled={true}
          onClose={close}
          onConfirm={confirm}
          t={t}
          title={t('Add Conversion Pixel')}
        >
          <Fieldset>
            <Input
              attribute={'name'}
              label={t('Conversion Pixel Name')}
              model={state.newSegment!}
              type={'text'}
            />
          </Fieldset>
        </ModalV1>
      )}
    </Page>
  );
}

export default observer(DataConversionPixelsPage);
